import React from "react";
import siteUrl from "./../../../const/const";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface ElementProps {
  question: any;
  index: number;
  opens: number[];
  keywords: string[];
  package: { id: number; name: string };
  toggleOpen: (id: number) => void;
}

const highlightText = (text: string, keyword: string): React.ReactNode => {
  if (!keyword) return text;
  const upper = text.toUpperCase();
  const kwUpper = keyword.toUpperCase();
  if (!upper.includes(kwUpper)) return text;
  const idx = upper.indexOf(kwUpper);
  return (
    <>
      {text.substring(0, idx)}
      <mark className="bg-yellow-200 dark:bg-yellow-300 text-gray-900 rounded-sm px-0.5">
        {text.substring(idx, idx + keyword.length)}
      </mark>
      {text.substring(idx + keyword.length)}
    </>
  );
};

const TextLines: React.FC<{ text: string; keyword: string }> = ({ text, keyword }) => (
  <>
    {text.split("/-/").map((m, i) => (
      <span key={i}>
        {highlightText(m, keyword)}
        <br />
      </span>
    ))}
  </>
);

const renderQuestionType = (values: any[]) => {
  switch (values.length) {
    case 2: return <span className="font-bold text-blue-500 dark:text-indigo-300"> Duplet</span>;
    case 3: return <span className="font-bold text-blue-500 dark:text-indigo-300"> Blits</span>;
    case 4: return <span className="font-bold text-blue-500 dark:text-indigo-300"> Kvadriplet</span>;
    default: return null;
  }
};

const Element: React.FC<ElementProps> = ({
  question: q,
  index,
  opens,
  keywords,
  package: pkg,
  toggleOpen,
}) => {
  const keyword = keywords?.[0] ?? "";
  const isOpen = opens.includes(q.values[0].id);
  const isMulti = q.values.length > 1;
  const hasBefore = Boolean(q.notebefore);

  return (
    <div className="border-b border-gray-100 dark:border-gray-800 py-4 last:border-0">
      {/* Package link */}
      <div className="mb-2">
        <Link
          to={`/packages/${pkg.id}`}
          className="text-lg font-bold text-blue-500 hover:underline dark:text-indigo-300"
        >
          {index + 1}.&nbsp;{pkg.name}
        </Link>
      </div>

      {/* Question type badge */}
      {isMulti && (
        <div className="mb-1 text-base">{renderQuestionType(q.values)}</div>
      )}

      {/* Shared rekvizit */}
      {q.isRekvizitForAll && (
        <div className="mb-2">
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Paylama materialı</p>
          {q.values[0].rekvizit?.text ? (
            <p className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
              {q.values[0].rekvizit.rekvizit.split("/-/").map((m: string, i: number) => (
                <span key={i}>{highlightText(m, keyword)}<br /></span>
              ))}
            </p>
          ) : q.values[0].rekvizit ? (
            <img
              src={`${siteUrl}images/${q.values[0].rekvizit.rekvizit}`}
              alt={q.values[0].rekvizit.rekvizit}
              className="max-w-full max-h-40 rounded-lg"
            />
          ) : null}
        </div>
      )}

      {/* Note before */}
      {q.notebefore && (
        <p className="text-gray-800 dark:text-gray-100 font-semibold mb-1">
          {highlightText(q.notebefore, keyword)}
        </p>
      )}

      {/* Values */}
      <div className="space-y-1">
        {q.values.map((qv: any, i: number) => {
          const rekOrNot = qv.rekvizit !== null;
          const rekTextOrNot = rekOrNot ? qv.rekvizit.text : false;
          const key = i + 1;

          return (
            <div key={i} className="text-gray-800 dark:text-gray-100 font-semibold break-words">
              {isMulti ? (
                rekOrNot && !q.isRekvizitForAll ? (
                  <p>{key}. Rekvizit</p>
                ) : (
                  <p>{key}. <TextLines text={qv.text} keyword={keyword} /></p>
                )
              ) : (
                !hasBefore ? (
                  rekOrNot ? (
                    <p><span className="font-bold">Paylama materialı</span></p>
                  ) : (
                    <p><TextLines text={qv.text} keyword={keyword} /></p>
                  )
                ) : (
                  rekOrNot ? (
                    <p>Paylama materialı</p>
                  ) : (
                    <p><TextLines text={qv.text} keyword={keyword} /></p>
                  )
                )
              )}

              {rekTextOrNot && !q.isRekvizitForAll && (
                <p className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 text-sm mt-1">
                  {qv.rekvizit.rekvizit.split("/-/").map((m: string, j: number) => (
                    <span key={j}>{highlightText(m, keyword)}<br /></span>
                  ))}
                </p>
              )}
              {rekOrNot && !q.isRekvizitForAll && !rekTextOrNot && (
                <img
                  src={`${siteUrl}images/${qv.rekvizit.rekvizit}`}
                  alt={qv.rekvizit.rekvizit}
                  className="max-w-full max-h-40 rounded-lg mt-1"
                />
              )}
              {rekOrNot && !q.isRekvizitForAll && (
                <p className="text-gray-800 dark:text-gray-100 font-semibold">
                  <TextLines text={qv.text} keyword={keyword} />
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Reveal toggle */}
      <button
        onClick={() => toggleOpen(q.values[0].id)}
        className="flex items-center gap-1 mt-2 text-blue-500 hover:text-blue-700 transition-colors dark:text-indigo-300 dark:hover:text-indigo-200"
        aria-label="Cavabı göstər / gizlət"
      >
        <ChevronDown
          className={cn(
            "w-4 h-4 transition-transform duration-300",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Collapsible reveal */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-300",
          isOpen ? "max-h-[2000px] opacity-100 mt-2" : "max-h-0 opacity-0"
        )}
      >
        <div className="space-y-1.5 text-sm">
          {q.values && (
            <p className="text-gray-700 dark:text-gray-200 font-medium">
              <span className="font-bold text-gray-900 dark:text-gray-50">Cavab: </span>
              {q.values.length > 1
                ? highlightText(`1. ${q.values[0].answer.trim()} `, keyword)
                : highlightText(q.values[0].answer.trim(), keyword)}
              {q.values[1] && <> {highlightText(`2. ${q.values[1].answer.trim()}`, keyword)}</>}
              {q.values[2] && <> {highlightText(`3. ${q.values[2].answer.trim()}`, keyword)}</>}
              {q.values[3] && <> {highlightText(`4. ${q.values[3].answer.trim()}`, keyword)}</>}
            </p>
          )}
          {q.considered && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">Sayılma meyarı: </span>
              {highlightText(q.considered, keyword)}
            </p>
          )}
          {q.comment && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">Şərh: </span>
              {highlightText(q.comment, keyword)}
            </p>
          )}
          {q.sources.length > 0 && (
            <p className="text-gray-700 dark:text-gray-200 break-all">
              <span className="font-bold text-gray-900 dark:text-gray-50">Mənbə: </span>
              {q.sources.map((src: string, i: number) => (
                <React.Fragment key={i}>
                  {i > 0 && <><br />&nbsp;&nbsp;&nbsp;&nbsp;</>}
                  {q.sources.length > 1 ? `${i + 1}. ${src}` : src}
                </React.Fragment>
              ))}
            </p>
          )}
          {q.authors.length > 0 && (
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-bold text-gray-900 dark:text-gray-50">
                {q.authors.length > 1 ? "Müəlliflər: " : "Müəllif: "}
              </span>
              {q.authors.map((a: any, i: number) => (
                <React.Fragment key={i}>
                  {i > 0 ? ", " : ""}
                  {a.fullname}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Element;
